import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import WorkspacesOutlined from '@mui/icons-material/WorkspacesOutlined';
import {
    CircularProgress,
    ListItemIcon,
    ListItemText,
    styled,
} from '@mui/material';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { MenuItemProps } from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import {
    apexLocalPath,
    getOperatorApexUrl,
    getOperatorTenantUrl,
    getOrgPathName,
} from '../../utils/env/urlStrings';
import { useAppSelector } from 'hooks';
import { selectUserData } from 'store/user/userSelector';
import { useGetUserOrgsQuery } from 'generated/graphql';
import { UserOrg } from 'graphql/types/userOrgs';
import { appRoutes } from 'app/routes/constants';
import useAuth from 'Modules/firebase/use-auth';

const StyledButton = styled(Button)(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--1, 0.5rem)',
    color: 'black !important',
}));

interface OrgNameButtonProps {
    organizationName: string;

    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const DefaultOrgNameButton = ({
    organizationName,
    onClick,
}: OrgNameButtonProps) => {
    const operatorApexUrl = useMemo(getOperatorApexUrl, []);

    return (
        <StyledButton
            onClick={(event) => {
                if (onClick) {
                    return onClick(event);
                }
                let redirectTo = `https://${operatorApexUrl}${appRoutes.AUTH_LOGIN}`;

                if (operatorApexUrl === apexLocalPath) {
                    redirectTo = `http://${operatorApexUrl}`;
                }

                window.open(redirectTo, '_blank');
            }}
        >
            <WorkspacesOutlined fontSize="small" />
            <Typography>{organizationName}</Typography>
        </StyledButton>
    );
};

interface OrgNameMenuItemProps {
    name: string;
    disabled?: boolean;
}

const OrgNameMenuItem = ({
    name,
    disabled,
    onClick,
}: MenuItemProps & OrgNameMenuItemProps) => (
    <MenuItem onClick={onClick} disabled={disabled}>
        <ListItemIcon>
            <WorkspacesOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText
            primary={name.toUpperCase()}
            primaryTypographyProps={{
                fontSize: '0.75rem',
                whiteSpace: 'break-spaces',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        />
    </MenuItem>
);

const OrgNameButton = memo((props: OrgNameButtonProps) => {
    const { organizationName } = props;
    const { logout } = useAuth();
    const currentOrganization = useMemo(
        () => ({
            displayName: organizationName,
            name: organizationName,
        }),
        [organizationName],
    );

    const { email } = useAppSelector(selectUserData);

    const { data, loading } = useGetUserOrgsQuery({
        variables: {
            email,
        },
    });

    const [userOrganizationsList, setUserOrganizationsList] = useState<
        UserOrg[]
    >([currentOrganization]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (
            data?.userOrganizations.length &&
            userOrganizationsList.length <= 1 &&
            !loading
        ) {
            const sortedOrgs = [
                currentOrganization,
                ...data.userOrganizations
                    .filter(({ tenantUrl }) => tenantUrl !== organizationName)
                    .sort((a, b) => a.displayName.localeCompare(b.displayName)),
            ];
            if (
                JSON.stringify(sortedOrgs) !==
                JSON.stringify(userOrganizationsList)
            ) {
                setUserOrganizationsList(sortedOrgs);
            }
        }
    }, [userOrganizationsList, loading, organizationName, currentOrganization]);

    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleOrgOptionClick = useCallback(
        (tenantName?: string) => {
            handleCloseMenu();

            if (tenantName) {
                // const tenantUrl = getOperatorTenantUrl(tenantName);
                // window.open(tenantUrl, '_blank');
                const navigateTo = getOrgPathName(tenantName);
                logout({
                    logoutParams: {
                        navigateTo
                    },
                });
            }
        },
        [handleCloseMenu],
    );

    const handleOpenMenu = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        },
        [],
    );

    return (
        <>
            <DefaultOrgNameButton {...props} onClick={handleOpenMenu} />
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                MenuListProps={{
                    sx: { width: anchorEl && anchorEl.offsetWidth },
                }}
                style={{
                    maxHeight: '270px',
                    overflowY: 'auto',
                }}
            >
                {userOrganizationsList.map((org) => (
                    <OrgNameMenuItem
                        key={org.displayName}
                        disabled={
                            org.displayName === currentOrganization.displayName
                        }
                        name={org.displayName}
                        onClick={() => handleOrgOptionClick(org.tenantUrl)}
                    />
                ))}
                {loading && (
                    <MenuItem disabled style={{ opacity: 1 }}>
                        <ListItemIcon
                            style={{
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <CircularProgress
                                variant="indeterminate"
                                size="0.75rem"
                                style={{ color: '#4C17AD' }}
                            />
                        </ListItemIcon>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
});

export default OrgNameButton;
